import { appConfig } from 'config/app'
import { useAxios } from '@/composables/useAxios'
import { useJwtStore } from '@/stores'
import { AvvAcceptResponse } from '@/types'

/**
 * The data processing agreement's document and its acceptance. Shared by the
 * AVV page and the consent screen, which offers the acceptance inline when an
 * approval waits for it.
 */

/** A request for the agreement document that was refused; 403 is an unverified e-mail address. */
export class AvvRequestError extends Error {
  constructor(readonly status: number) {
    super(`AVV request answered ${status}`)
    this.name = 'AvvRequestError'
  }
}

/**
 * The agreement as a PDF: the stored copy once accepted, otherwise a preview of
 * the current version filled in with the account's data. Fetched rather than
 * linked, because the request carries the session's bearer token. Typed as a
 * PDF whatever the response says, because the blob is framed on our origin and
 * any other body, such as a proxy's error page, would render there as a page.
 */
export const fetchAvvPdf = async (accepted: boolean): Promise<Blob> => {
  const query = accepted ? '' : '?preview=true'
  const response = await fetch(`${appConfig.env.baseUrl}/account/av${query}`, {
    headers: { Authorization: `Bearer ${useJwtStore().getJwt}` }
  })
  if (!response.ok) throw new AvvRequestError(response.status)
  return new Blob([await response.arrayBuffer()], { type: 'application/pdf' })
}

/** Accepts the current version for the signed-in account; null for an answer of an unexpected shape. */
export const acceptAvv = async (): Promise<AvvAcceptResponse | null> => {
  const res = await useAxios<null, AvvAcceptResponse>('POST', '/account/av/accept', true)
  const parsed = AvvAcceptResponse.safeParse(res.data)
  return parsed.success ? parsed.data : null
}
